import { Grid, TextField, Typography } from '@material-ui/core'
import React from 'react'

import SectionTitle from '../../../components/SectionTitle'
import { convertPersianNumbersToEnglishNumbers } from '../../../helpers/numbers'
import { getTranslate } from '../../../localization'
import useStyles from './styles/section.styles'

interface LimitsSectionProps {
  maxRedemptions: string
  maxDiscountBudgetMinor: string
  maxRedemptionsPerGuest: string
  currency: string
  onChange: (
    key: 'maxRedemptions' | 'maxDiscountBudgetMinor' | 'maxRedemptionsPerGuest',
    value: string
  ) => void
  fieldErrors: Record<string, string>
}

/**
 * How much the promotion may give away in total.
 *
 * This is the money surface and it had no controls at all: a capped campaign and an
 * uncapped one looked identical on screen, and the caps were carried invisibly through
 * every save.
 *
 * The budget is shown in MINOR UNITS, labelled, not converted — following the app's own
 * rule rather than a fresh judgement. `helpers/promotions.ts` states that fixed-amount
 * values are deliberately not converted and renders money as "5000000 IRR (minor units)";
 * only percent is converted, because a percent is a ratio and not money. Converting here
 * would make this field disagree with the discount field two sections above it.
 */
const LimitsSection: React.FC<LimitsSectionProps> = ({
  maxRedemptions,
  maxDiscountBudgetMinor,
  maxRedemptionsPerGuest,
  currency,
  onChange,
  fieldErrors
}) => {
  const classes = useStyles()

  const numeric = (
    key: 'maxRedemptions' | 'maxDiscountBudgetMinor' | 'maxRedemptionsPerGuest',
    label: string,
    value: string,
    helper: string
  ) => (
    <TextField
      fullWidth
      size="small"
      type="text"
      inputMode="numeric"
      id={key}
      name={key}
      label={getTranslate(label)}
      value={value}
      error={Boolean(fieldErrors[key])}
      helperText={fieldErrors[key] || helper}
      onChange={(event) =>
        onChange(key, convertPersianNumbersToEnglishNumbers(event.target.value))
      }
    />
  )

  return (
    <div className={classes.section}>
      <SectionTitle title="Limits" marginBottom />
      <Typography variant="caption" className={classes.note}>
        {getTranslate(
          'An empty field means the promotion is uncapped in that dimension.'
        )}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          {numeric(
            'maxRedemptions',
            'Max redemptions',
            maxRedemptions,
            getTranslate('Total times this promotion may be applied.')
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {numeric(
            'maxDiscountBudgetMinor',
            'Total discount budget',
            maxDiscountBudgetMinor,
            getTranslate('In {currency} minor units.', { currency })
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {numeric(
            'maxRedemptionsPerGuest',
            'Per-guest limit',
            maxRedemptionsPerGuest,
            getTranslate('Requires guest identification at booking.')
          )}
        </Grid>
      </Grid>
    </div>
  )
}

export default LimitsSection
